/**
 * Unified Flexible Header Component
 * Can be used with simple title or complex left/right content
 * Follows Pixel Brutalism design system from BudgetHeader
 */

import { ReactNode } from 'react';
import { Menu, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProfileMenu } from './ProfileMenu';
import { useLayout } from '../contexts/LayoutContext';

interface HeaderProps {
  /** Simple title text (alternative to leftContent) */
  title?: string;
  /** Custom content for the left side (overrides title if provided) */
  leftContent?: ReactNode;
  /** Optional content for the right side (buttons, actions, etc.) */
  rightContent?: ReactNode;
  /** Optional custom max width class (default: max-w-2xl) */
  maxWidth?: string;
  /** Hide profile menu (default: false) */
  hideProfileMenu?: boolean;
}

export default function Header({
  title,
  leftContent,
  rightContent,
  maxWidth = 'max-w-2xl',
  hideProfileMenu = false
}: HeaderProps) {
  const { t } = useTranslation();
  const { toggleSidebar } = useLayout();
  const navigate = useNavigate();

  // Use leftContent if provided, otherwise use title as default
  const renderedLeftContent = leftContent || (
    title ? <h1 className="text-2xl font-bold tracking-tight">{title}</h1> : null
  );

  return (
    <header className="bg-[#3cbe8c] text-white border-b-4 border-[#077f5d] px-4 lg:px-6 py-3 lg:py-2 pr-1 min-h-none sticky top-0 z-50">
      <div className={`${maxWidth} lg:max-w-none mx-auto flex items-center justify-between min-h-[48px]`}>
        {/* Left Side: Burger + Custom Content or Title */}
        <div className="flex items-center gap-4 flex-1 min-w-0 mr-4">
          {/* Burger Button - Desktop Only */}
          <button 
            onClick={toggleSidebar}
            className="hidden lg:flex p-2 hover:bg-[#2ea876] rounded-lg transition-colors"
            aria-label="Toggle Sidebar"
          >
            <Menu className="w-6 h-6 text-white" />
          </button>

          <div className="flex flex-col gap-1 flex-1 min-w-0">
            {renderedLeftContent}
          </div>
        </div>

        {/* Right Side: Custom Content + Profile Menu */}
        <div className="flex items-center">
          {rightContent && (
            <div className="flex items-center">
              {rightContent}
            </div>
          )}
          
          {/* Desktop: Add Transaction Button */}
          <button
            onClick={() => navigate('/add')}
            className="hidden lg:flex items-center p-2 ml-2 bg-[#3abf8c] text-white border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '8px',
              boxShadow: '4px 4px 0 #077f5d',
              transition: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <Plus className="pr-2" size={24} />
            {t('nav.add_transaction')}
          </button>

          {/* Mobile: Profile Menu (Three Dots) */}
          {!hideProfileMenu && (
            <div className="lg:hidden">
              <ProfileMenu />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
